import type { AnalyticsInstrumentationSubscriber } from "@/core/analytics/analytics.events";
import type { GtagConsentParams } from "@/core/analytics/gtm/gtm.types";
import type { ConsentDecision } from "@/core/consent/domain";

const grant = (allowed: boolean): "granted" | "denied" => (allowed ? "granted" : "denied");

/**
 * The engine's decision as Google Consent Mode flags. The mapping is the one
 * `GoogleTagManager` has always relayed: advertising drives the three `ad_*`
 * flags, analytics drives `analytics_storage`, functional drives both
 * `functionality_storage` and `personalization_storage`. `security_storage`
 * is always granted — it is strictly-necessary by Google's own definition
 * and the layout's defaults script grants it up front.
 */
export const toGtagConsentParams = (decision: ConsentDecision): GtagConsentParams => ({
  ad_storage: grant(decision.advertising),
  ad_user_data: grant(decision.advertising),
  ad_personalization: grant(decision.advertising),
  analytics_storage: grant(decision.analytics),
  functionality_storage: grant(decision.functional),
  personalization_storage: grant(decision.functional),
  security_storage: "granted",
});

/**
 * Google Tag Manager as a `necessary` subscriber — a RELAY, not a load gate.
 *
 * GTM's pattern is correct as it stands and stays: the root layout's inline
 * script sets Consent Mode defaults to denied before anything loads, the
 * container snippet loads for every visitor, and every tag inside it
 * (Meta Pixel included, under the `ad_*` flags) fires only once Consent Mode
 * says it may. What the sink owns is the relay: on every resolved
 * `consent.changed` it pushes `gtag('consent', 'update', …)` with the
 * engine's effective decision, so a GPC clamp or a later revoke reaches
 * GTM exactly like a banner answer. Nothing is relayed while unresolved —
 * the defaults already say denied, and a fabricated update would only
 * repeat them.
 *
 * `necessary` because the relay itself must be live before any decision:
 * it is the thing that carries the decision. There is exactly ONE relay on
 * the page — `GoogleTagManager`'s own consent effect stands down on the
 * new-stack build.
 */
export const createGtmSink = (): AnalyticsInstrumentationSubscriber => ({
  name: "gtm",
  requiredScope: "necessary",
  activate: () => {},
  handle: (event) => {
    if (event.topic !== "consent.changed" || event.payload.status !== "resolved") {
      return;
    }

    // `gtag` is the layout's defaults shim; absent when GTM is not configured
    // for the deployment, in which case there is nothing to relay to.
    window.gtag?.("consent", "update", toGtagConsentParams(event.payload.decision));
  },
});
